"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { AccessibilityStatement } from "../../types/statement";
import { Plus } from "lucide-react";
import { HelpText } from "../help-text";
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";

interface FeedbackStepProps {
  data: AccessibilityStatement["feedback"];
  onChange: (data: AccessibilityStatement["feedback"], changedField?: keyof AccessibilityStatement["feedback"]) => void;
  publicationDate: string;
  onPublicationDateChange: (date: string) => void;
}

export function FeedbackStep({
  data,
  onChange,
  publicationDate,
  onPublicationDateChange,
}: FeedbackStepProps) {
  const handleChange = (
    field: keyof AccessibilityStatement["feedback"],
    value: string | boolean
  ) => {
    if (field === "sameAsVisitorAddress" && value === true) {
      onChange({
        ...data,
        sameAsVisitorAddress: true,
        companyPostalAddress: data.companyVisitorAddress,
      }, field);
    } else if (field === "sameAsVisitorAddress" && value === false) {
      onChange({
        ...data,
        sameAsVisitorAddress: false,
        companyPostalAddress: "",
      }, field);
    } else if (field === "companyVisitorAddress" && data.sameAsVisitorAddress) {
      onChange({
        ...data,
        companyVisitorAddress: value as string,
        companyPostalAddress: value as string,
      }, field);
    } else {
      onChange({ ...data, [field]: value }, field);
    }
  };

  // Helper to update a specific enforcement team
  const handleEnforcementTeamChange = (
    index: number,
    field: keyof AccessibilityStatement["feedback"]["enforcementTeam"][0],
    value: string
  ) => {
    const updatedTeams = data.enforcementTeam.map((team, idx) =>
      idx === index ? { ...team, [field]: value } : team
    );
    onChange({ ...data, enforcementTeam: updatedTeams }, "enforcementTeam");
  };

  // Add a new enforcement team
  const addEnforcementTeam = () => {
    onChange({
      ...data,
      enforcementTeam: [
        ...data.enforcementTeam,
        { name: "", linkToEnforcementProcedure: "", contactDetails: "" },
      ],
    }, "enforcementTeam");
  };

  return (
    <div className="cy:space-y-6">
      <div>
        <h2 className="cy:text-[28px] cy:font-semibold cy:mb-10">
          Feedback options
        </h2>
      </div>
      <div className="cy:space-y-4">
        <div>
          <Label
            htmlFor="companyPhoneNumber"
            className="cy:text-lg cy:font-semibold"
          >
            Company phone number
          </Label>
          <HelpText>Example: +12 34 567 89 00</HelpText>
          <Input
            id="companyPhoneNumber"
            type="tel"
            value={data.companyPhoneNumber}
            onChange={(e) => handleChange("companyPhoneNumber", e.target.value)}
            className="cy:mt-1"
          />
        </div>
        <div>
          <Label
            htmlFor="companyEmailAddress"
            className="cy:text-lg cy:font-semibold"
          >
            Company email address
          </Label>
          <HelpText>Example: accessibility@example.com</HelpText>
          <Input
            id="companyEmailAddress"
            type="email"
            value={data.companyEmailAddress}
            onChange={(e) =>
              handleChange("companyEmailAddress", e.target.value)
            }
            className="cy:mt-1"
          />
        </div>
        <div>
          <Label
            htmlFor="companyVisitorAddress"
            className="cy:text-lg cy:font-semibold"
          >
            Company visitor address
          </Label>
          <HelpText>Example: Sample Street 1, 234 Example House</HelpText>
          <Textarea
            id="companyVisitorAddress"
            value={data.companyVisitorAddress}
            onChange={(e) =>
              handleChange("companyVisitorAddress", e.target.value)
            }
            className="cy:mt-2 cy:h-[118px] cy:rounded cy:text-base!"
            rows={3}
          />
        </div>
        <div>
          <Label
            htmlFor="companyPostalAddress"
            className="cy:text-lg cy:font-semibold"
          >
            Company postal address
          </Label>
          <HelpText>Example: PO Box1, 234 Example House</HelpText>
          <div className="cy:flex cy:items-center cy:mb-1 cy:mt-1">
            <input
              id="sameAsVisitorAddress"
              type="checkbox"
              checked={data.sameAsVisitorAddress}
              onChange={(e) =>
                handleChange("sameAsVisitorAddress", e.target.checked)
              }
              className="cy:mr-2"
            />
            <Label
              htmlFor="sameAsVisitorAddress"
              className="cy:text-sm cy:font-normal cy:mb-0!"
            >
              Same as visitor address
            </Label>
          </div>
          <Textarea
            id="companyPostalAddress"
            value={data.companyPostalAddress}
            onChange={(e) =>
              handleChange("companyPostalAddress", e.target.value)
            }
            className="cy:mt-2 cy:h-[118px] cy:rounded cy:text-base!"
            rows={3}
            disabled={data.sameAsVisitorAddress}
          />
        </div>
        <div>
          <Label
            htmlFor="otherContactOptions"
            className="cy:text-lg cy:font-semibold"
          >
            Other contact options
          </Label>
          <HelpText>Example: on social media; Twitter: @ExampleUser</HelpText>
          <Input
            id="otherContactOptions"
            value={data.otherContactOptions}
            onChange={(e) =>
              handleChange("otherContactOptions", e.target.value)
            }
            className="cy:mt-1"
          />
        </div>
        <div>
          <Label
            htmlFor="typicalDurationForResponse"
            className="cy:text-lg cy:font-semibold"
          >
            Typical duration for response
          </Label>
          <HelpText>Example: 2 business days</HelpText>
          <Input
            id="typicalDurationForResponse"
            value={data.typicalDurationForResponse}
            onChange={(e) =>
              handleChange("typicalDurationForResponse", e.target.value)
            }
            className="cy:mt-1"
          />
        </div>
        <hr className="cy:my-4 cy:mt-6" />
        <div>
          <h3 className="cy:text-lg cy:font-semibold cy:mb-1">Formal complaints</h3>
          <div className="cy:flex cy:p-2 cy:rounded-sm cy:mb-2 cy:bg-[#2684ff26] cy:border cy:border-[#2684ff40]">
            <HelpText>Note : If you don’t receive a timely or adequate response to their accessibility issue.</HelpText>
          </div>
        </div>
        {data.enforcementTeam.map((team, idx) => (
          <div
            key={idx}
            className=""
          >
            <div className="cy:mt-4">
              <Label
                htmlFor={`enforcementTeam-name-${idx}`}
                className="cy:text-lg cy:font-semibold"
              >
                Name of the enforcement team
              </Label>
              <HelpText>Example:  European Union Agency for Fundamental Rights (FRA)</HelpText>
              <Input
                id={`enforcementTeam-name-${idx}`}
                value={team.name}
                onChange={(e) =>
                  handleEnforcementTeamChange(idx, "name", e.target.value)
                }
                className="cy:mt-1"
              />
            </div>
            <div className="cy:mt-4">
              <Label
                htmlFor={`enforcementTeam-link-${idx}`}
                className="cy:text-lg cy:font-semibold cy:flex! cy:gap-1"
              >
                Link to the enforcement authority
                <TooltipWrapper content="Provide a URL to the relevant national body’s complaint procedure page or form, if available." />
              </Label>
              <HelpText>Example:  https://example.org/eaa-enforcement-team</HelpText>
              <Input
                id={`enforcementTeam-link-${idx}`} 
                value={team.linkToEnforcementProcedure}
                onChange={(e) =>
                  handleEnforcementTeamChange(
                    idx,
                    "linkToEnforcementProcedure",
                    e.target.value
                  )
                }
                className="cy:mt-1"
              />
            </div>
            <div className="cy:mt-4">
              <Label
                htmlFor={`enforcementTeam-contact-${idx}`}
                className="cy:text-lg cy:font-semibold cy:flex! cy:gap-1"
              >
                Contact information for the enforcement body
                <TooltipWrapper content="This might be a digital accessibility monitoring agency, ombudsman, or equality authority, depending on your country." />
              </Label>
              <HelpText>Example: <br /><br />
              Email : mail@example.com <br />
              Contact : 00 800 67 89 10 11 (from inside the EU) <br />
              +32 2 299 96 96 (from outside the EU)</HelpText>
              <Textarea
                id={`enforcementTeam-contact-${idx}`}
                value={team.contactDetails}
                onChange={(e) =>
                  handleEnforcementTeamChange(
                    idx,
                    "contactDetails",
                    e.target.value
                  )
                }
                className="cy:mt-2 cy:h-[160px] cy:rounded cy:text-base!"
                rows={6}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addEnforcementTeam}
          className="cy:flex cy:items-center cy:gap-2 cy:mt-2  cy:text-base cy:font-normal cy:focus:outline-none cy:mb-4"
        >
          <span className="cy:w-5 cy:h-5 cy:bg-blue-600 cy:text-white cy:rounded-full cy:flex cy:items-center cy:justify-center cy:mr-1">
            <Plus className="cy:w-3 cy:h-3" />
          </span>
          Add another enforcement team details
        </button>
      </div>
      <div>
          <Label
            htmlFor="publicationDate"
            className="cy:text-lg cy:font-semibold cy:flex! cy:items-center cy:gap-1"
          >
            Date of publication
            <TooltipWrapper content="Providing the date of your accessibility statement helps users know if it's up to date. Statements older than a year may seem unmaintained. To avoid confusion with international date formats, use the full date (e.g., '1 February 2019' instead of '01/02/2019'" />
          </Label>
          <HelpText>Example:  01 July 2025</HelpText>
          <Input
            id="publicationDate"
            type="date"
            value={publicationDate}
            onChange={(e) => onPublicationDateChange(e.target.value)}
            className="cy:mt-1"
          />
        </div>
    </div>
  );
}
